import * as React from 'react';
import { getGlobalEventsLogs } from '@cian/events-log';

import {
  selectHasMore,
  selectPaginationStatus,
  selectRecommendations,
  selectRecommendationsStatus,
  selectRecommendationsTotalCount,
} from '../../selectors/recommendations';
import { selectShowHeader } from '../../selectors/showHeader';
import { fetchMoreRecommendations, fetchPuids, fetchRecommendations } from '../../slices';
import { useAppDispatch, useAppSelector as useSelector } from '../../types';
import { useApplicationContext } from '../../utils/applicationContext';
import { selectRegionId } from '../../selectors/selectRegionId';
import { selectLandingType } from '../../selectors/landingType';
import { trackMoreButtonClick, trackRecommendationItemClick, trackRecommendationsView } from './tracking';
import { TAnalyticsData, TOfferAnalyticsData, TProduct, TTrapAnalyticsData } from '../../types/recommendation';
import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { selectShowFullList } from '../../selectors/showFullList';
import { EPaginationStatus } from '../../slices/recommendationsSlice';
import { ELoadingStatus } from '../../types/loadingStatus';

const TOPIC_RECOMMENDATIONS = 'recommendations';

/**
 * Инициализирующая загрузка рекомендаций и puids.
 * Должен вызываться строго один раз — в корневом контейнере,
 * чтобы избежать повторных диспатчей при использовании `useRecommendations`
 * в нескольких компонентах одновременно.
 */
export const useInitRecommendations = () => {
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(fetchPuids());
    dispatch(fetchRecommendations());
  }, [dispatch]);
};

export const useRecommendations = () => {
  const {
    custom: { isMobile },
  } = useApplicationContext();

  const { setOfferVisited, isOfferVisited: isVisited } = useVisitedOffersContext();

  const isOfferVisited = React.useCallback((id: number | null) => !!id && isVisited(id), [isVisited]);

  const dispatch = useAppDispatch();
  const recommendations = useSelector(selectRecommendations);
  const recommendationsStatus = useSelector(selectRecommendationsStatus);
  const hasMore = useSelector(selectHasMore);
  const recommendationsTotalCount = useSelector(selectRecommendationsTotalCount);
  const paginationStatus = useSelector(selectPaginationStatus);
  const showHeader = useSelector(selectShowHeader);
  /**
   * @todo Удалить после завершения эксперимента
   * @description Данный функционал появился в задаче CD-253014, будет удалён в задаче 	CD-253668
   */
  const showFullList = useSelector(selectShowFullList);
  const regionId = useSelector(selectRegionId);
  const landingType = useSelector(selectLandingType);
  const shownRecommendationIdsRef = React.useRef(new Set<number>());

  const onRecommendationClick = React.useCallback(
    (analyticsData: TAnalyticsData) => {
      const globalLog = getGlobalEventsLogs();

      if (analyticsData.type === 'trap') {
        globalLog.produce(TOPIC_RECOMMENDATIONS, {
          type: 'click',
          value: analyticsData,
        });

        return;
      } else {
        setOfferVisited(analyticsData.offerId);
      }

      trackRecommendationItemClick(landingType, analyticsData.tag, analyticsData.products);
    },
    [landingType, setOfferVisited],
  );

  const onShowTrap = React.useCallback((analyticsData: TTrapAnalyticsData) => {
    const globalLog = getGlobalEventsLogs();

    globalLog.produce(TOPIC_RECOMMENDATIONS, {
      type: 'showTrap',
      value: analyticsData,
    });
  }, []);

  const onShow = React.useCallback(() => {
    const products = recommendations
      .map(({ offerId: id }, index) => ({
        id,
        position: index + 1,
        offerType: 'offer',
        extra: isOfferVisited(id) ? { is_viewes: true } : undefined,
      }))
      .filter(({ id }) => {
        if (!id || shownRecommendationIdsRef.current.has(id)) {
          return false;
        }

        shownRecommendationIdsRef.current.add(id);

        return true;
      });

    if (!products.length) {
      return;
    }

    const modelVersion = (recommendations[0].analyticsData as TOfferAnalyticsData).tag;

    trackRecommendationsView(landingType, modelVersion, products as TProduct[]);
  }, [recommendations, landingType, isOfferVisited]);

  const onMoreClick = React.useCallback(() => {
    const modelVersion = (recommendations[0].analyticsData as TOfferAnalyticsData).tag;

    trackMoreButtonClick(landingType, modelVersion);
  }, [landingType, recommendations]);

  const onLoadMore = React.useCallback(() => {
    if (!hasMore || paginationStatus === EPaginationStatus.Loading) {
      return;
    }

    dispatch(fetchMoreRecommendations());
  }, [dispatch, hasMore, paginationStatus]);

  const onRetryLoadMore = React.useCallback(() => {
    if (paginationStatus !== EPaginationStatus.Error) {
      return;
    }

    dispatch(fetchMoreRecommendations());
  }, [dispatch, paginationStatus]);

  const onRetryRecommendations = React.useCallback(() => {
    if (recommendationsStatus !== ELoadingStatus.Failure) {
      return;
    }

    dispatch(fetchRecommendations());
  }, [dispatch, recommendationsStatus]);

  return React.useMemo(
    () => ({
      recommendations,
      showHeader,
      onShowTrap,
      onShow,
      onRecommendationClick,
      recommendationsStatus,
      hasMore,
      recommendationsTotalCount,
      paginationStatus,
      onLoadMore,
      onRetryLoadMore,
      onRetryRecommendations,
      isMobile,
      regionId,
      onMoreClick,
      isOfferVisited,
      showFullList,
    }),
    [
      recommendations,
      showHeader,
      onShowTrap,
      onShow,
      onRecommendationClick,
      recommendationsStatus,
      hasMore,
      recommendationsTotalCount,
      paginationStatus,
      onLoadMore,
      onRetryLoadMore,
      onRetryRecommendations,
      isMobile,
      regionId,
      onMoreClick,
      isOfferVisited,
      showFullList,
    ],
  );
};
